import { Directive, inject, input, TemplateRef } from '@angular/core'

/** A display row with stable identity and an optional fixed height. */
export interface MobileListRow<T> {
  id: string
  value: T
  /** Fixed height in pixels. Defaults to the list's rowHeight. */
  height?: number
  kind?: 'item' | 'header' | 'add'
  /** Extra left inset in pixels for nested content and its adjacent dividers.
   * Apply mobile-list-row-surface to the projected row root to inset its content.
   */
  indent?: number
  /** Skips item feedback and dividers for a toolbar or other supporting row. */
  auxiliary?: boolean
  /** Persistent caller-owned highlight, separate from a temporary press. */
  highlighted?: boolean
}

export interface MobileListRowContext<T> {
  $implicit: T
  index: number
}

/** Types the projected row template from the same rows supplied to the list. */
@Directive({ selector: 'ng-template[awMobileListRow]' })
export class MobileListRowDirective<T> {
  readonly awMobileListRow = input.required<readonly MobileListRow<T>[]>()
  readonly template = inject<TemplateRef<MobileListRowContext<T>>>(TemplateRef)

  /**
   * Narrows template variables to the list's content type.
   * @param {MobileListRowDirective<T>} directive Row template declaration.
   * @param {unknown} context Angular template context.
   * @returns {context is MobileListRowContext<T>} Whether the context belongs to this template.
   */
  static ngTemplateContextGuard<T>(
    directive: MobileListRowDirective<T>,
    context: unknown
  ): context is MobileListRowContext<T> {
    return true
  }
}
